import { useKnockClient } from "@knocklabs/react";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { addDays } from "date-fns";
import { useCallback } from "react";

import {
  KNOCK_LAST_DECLINED_REVIEW_PROMPT_AT_ATTRIBUTE,
  KNOCK_LAST_DISMISSED_REVIEW_PROMPT_AT_ATTRIBUTE,
  KNOCK_REVIEW_PROMPT_DECLINE_COUNT_ATTRIBUTE,
  KNOCK_REVIEW_PROMPT_DISMISS_COUNT_ATTRIBUTE,
  KNOCK_REVIEW_PROMPT_NEXT_ELIGIBLE_AT_ATTRIBUTE,
  REVIEW_PROMPT_DISMISS_BACKOFF_DAYS,
} from "./constants";

interface ReviewPromptCooldowns {
  /**
   * Decline write: reads `reviewPromptDeclineCount`, then writes the
   * incremented count plus `lastDeclinedReviewPromptAt` in a single identify
   * call. Called on the No tap, before the feedback step opens, so a user
   * who bails (closes the app) before Submit/Cancel is still excluded from
   * re-prompts. Does not touch `reviewPromptNextEligibleAt` — that attribute
   * stays scoped to the dismiss path. The dashboard targeting rule gates the
   * decline cooldown off `lastDeclinedReviewPromptAt` directly. A failed read
   * falls back to writing count=1.
   */
  identifyDeclineCooldown: () => Promise<void>;
  /**
   * Dismiss cooldown with stepped backoff. Reads the current
   * `reviewPromptDismissCount`, picks the matching step of
   * `REVIEW_PROMPT_DISMISS_BACKOFF_DAYS`, then writes the incremented count,
   * the historical "last dismissed" marker, and the new next-eligible
   * timestamp. A failed read falls back to step 0 rather than dropping the
   * write.
   */
  identifyDismissCooldown: () => Promise<void>;
}

export function useReviewPromptCooldowns(): ReviewPromptCooldowns {
  const knockClient = useKnockClient();

  const identifyDeclineCooldown = useCallback(async () => {
    const now = new Date();
    let currentCount = 0;
    try {
      const user = await knockClient.user.get();
      const stored = user[KNOCK_REVIEW_PROMPT_DECLINE_COUNT_ATTRIBUTE];
      if (typeof stored === "number" && Number.isFinite(stored) && stored >= 0) {
        currentCount = Math.floor(stored);
      }
    } catch (error) {
      logError(APP_V2_APP_EVENTS.RATING_PROMPT_KNOCK_IDENTIFY_FAILED, { error });
    }

    try {
      await knockClient.user.identify({
        [KNOCK_REVIEW_PROMPT_DECLINE_COUNT_ATTRIBUTE]: currentCount + 1,
        [KNOCK_LAST_DECLINED_REVIEW_PROMPT_AT_ATTRIBUTE]: now.toISOString(),
      });
    } catch (error) {
      logError(APP_V2_APP_EVENTS.RATING_PROMPT_KNOCK_IDENTIFY_FAILED, { error });
    }
  }, [knockClient]);

  const identifyDismissCooldown = useCallback(async () => {
    const now = new Date();
    let currentCount = 0;
    try {
      const user = await knockClient.user.get();
      const stored = user[KNOCK_REVIEW_PROMPT_DISMISS_COUNT_ATTRIBUTE];
      if (typeof stored === "number" && Number.isFinite(stored) && stored >= 0) {
        currentCount = Math.floor(stored);
      }
    } catch (error) {
      logError(APP_V2_APP_EVENTS.RATING_PROMPT_KNOCK_IDENTIFY_FAILED, { error });
    }

    const stepIndex = Math.min(currentCount, REVIEW_PROMPT_DISMISS_BACKOFF_DAYS.length - 1);
    const backoffDays = REVIEW_PROMPT_DISMISS_BACKOFF_DAYS[stepIndex];

    try {
      await knockClient.user.identify({
        [KNOCK_REVIEW_PROMPT_DISMISS_COUNT_ATTRIBUTE]: currentCount + 1,
        [KNOCK_LAST_DISMISSED_REVIEW_PROMPT_AT_ATTRIBUTE]: now.toISOString(),
        [KNOCK_REVIEW_PROMPT_NEXT_ELIGIBLE_AT_ATTRIBUTE]: addDays(now, backoffDays).toISOString(),
      });
    } catch (error) {
      logError(APP_V2_APP_EVENTS.RATING_PROMPT_KNOCK_IDENTIFY_FAILED, { error });
    }
  }, [knockClient]);

  return { identifyDeclineCooldown, identifyDismissCooldown };
}
